/**
 * Schémas Zod du métier IT – sous-module Incidents (it.demandes.incidents). Même convention que schemas.ts : types
 * déduits (api-types.ts), entrées validées par l'API avec ces schémas, réutilisables par les formulaires du front.
 * Règles métier (statuts, actions, SLA) : it/incidents.ts.
 */
import { z } from 'zod';
import { STATUTS_INCIDENT, PRIORITES_INCIDENT, ETATS_SLA, SLA_DELAI_MAX_MIN } from './it/incidents';
import { CIBLES_CORRECTION } from './it/corrections';
import { LANGUES } from './langues';
import { schemaCodeFiliale, schemaDateIso, schemaIdsDepots, schemaPieceJointe, schemaVisibilite } from './schemas';

// ------------------------------------------------------------------ pièces jointes (dépôt différé du socle)
/**
 * Clés du registre des pièces jointes (api/src/pj/objets.ts) : fichiers de la déclaration (clé = le sous-module) et
 * fichiers d'un commentaire (sorte d'objet propre, droits portés par le nœud it.demandes.incidents). Clés de registre
 * seulement : jamais une ressource Cedar ni une entrée du catalogue.
 */
export const CLE_PJ_INCIDENT = 'it.demandes.incidents';
export const CLE_PJ_COMMENTAIRES_INCIDENT = 'it.demandes.incidents.commentaires';

/**
 * Nombre maximal de fichiers joints à une déclaration ou à un commentaire.
 *
 * Plafond du socle (MAX_DEPOTS_DIFFERES) ; taille et types : ceux du socle (D-IT-120, D-IT-083).
 */
export const PIECES_JOINTES_INCIDENT_MAX = 10;

// ------------------------------------------------------------------ briques
/** E-mail d'un utilisateur AGORA, en minuscules (forme unique du socle, contrainte des colonnes demandeur / affecte_a). */
export const schemaEmailUtilisateur = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().max(254).regex(/^[^@\s]+@[^@\s]+$/, 'E-mail attendu'));

export const schemaStatutIncident = z.enum(STATUTS_INCIDENT);
/** Priorité 1 (la plus urgente) à 4 ; accepte aussi la forme texte d'un paramètre d'adresse. */
export const schemaPrioriteIncident = z.coerce.number().pipe(z.literal([...PRIORITES_INCIDENT]));
export const NIVEAUX_INCIDENT = ['eleve', 'moyen', 'faible'] as const;
/** Impact et urgence, saisis à la qualification. */
export const schemaNiveauIncident = z.enum(NIVEAUX_INCIDENT);
export const schemaEtatSla = z.enum(ETATS_SLA);

/** Liste en paramètre d'adresse : « a,b » ou tableau (outil MCP). */
const liste = <T extends z.ZodType>(element: T) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v),
    z.array(element).min(1).max(10),
  );

/** Booléen en paramètre d'adresse (« true » / « false ») ou en JSON. */
const booleen = z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean());

// ------------------------------------------------------------------ entrées
/** Longueurs du titre (contrainte de it.incident.titre) et de la description, à la déclaration comme à la correction. */
export const TITRE_INCIDENT_MAX = 200;
export const DESCRIPTION_INCIDENT_MAX = 20_000;

/**
 * Déclaration d'un incident, pour soi ou pour un tiers (`demandeur` : e-mail d'un utilisateur AGORA, réservé aux agents ;
 * son nom est lu dans l'annuaire, jamais saisi). Filiale précise : la vue consolidée est en lecture seule.
 */
export const schemaIncidentNouveau = z
  .object({
    filiale: schemaCodeFiliale,
    titre: z.string().trim().min(1, 'Obligatoire').max(TITRE_INCIDENT_MAX),
    description: z.string().trim().min(1, 'Obligatoire').max(DESCRIPTION_INCIDENT_MAX),
    categorieId: z.guid(),
    demandeur: schemaEmailUtilisateur.optional(),
    /** Dépôts différés (SelectionPiecesJointes), consommés par la déclaration. */
    piecesJointes: schemaIdsDepots(PIECES_JOINTES_INCIDENT_MAX).optional(),
  })
  .strict();

// ------------------------------------------------------------------ agent vocal du support IT (D-IT-161)
/**
 * Routes /v1/integrations/agent-vocal (api/src/integrations/agent-vocal.ts) : l'agent identifie l'appelant (numéro,
 * sinon nom), lit les catégories puis déclare pour lui. `appelant` = identifiant renvoyé par l'identification.
 * Numéro facultatif : un appel sans numéro présenté (ou un essai depuis le navigateur) est simplement non reconnu.
 */
export const schemaAppelantAgentVocal = z
  .object({
    numero: z.string().trim().max(64).optional(),
    /**
     * Conversation de l'agent (ElevenLabs : system__conversation_id ; Connect : contact) : le numéro présenté y est
     * retenu, puis cité dans le ticket créé dans la même conversation.
     */
    conversation: z.string().trim().max(200).optional(),
  })
  .strict();
export const schemaSalariesAgentVocal = z.object({ nom: z.string().trim().min(2).max(100) }).strict();
const schemaIdAppelant = z.string().trim().min(1).max(128);
export const schemaCategoriesAgentVocal = z
  .object({ appelant: schemaIdAppelant, langue: z.enum(LANGUES).optional() })
  .strict();
/** Place gardée en fin de description pour la trace du canal (it.incidents.agentVocal.trace). */
export const TRACE_AGENT_VOCAL_MAX = 500;
export const schemaDeclarationAgentVocal = z
  .object({
    appelant: schemaIdAppelant,
    titre: z.string().trim().min(1, 'Obligatoire').max(TITRE_INCIDENT_MAX),
    description: z.string().trim().min(1, 'Obligatoire').max(DESCRIPTION_INCIDENT_MAX - TRACE_AGENT_VOCAL_MAX),
    categorieId: z.guid(),
    /** Filiale de l'incident : obligatoire seulement si l'appelant en a plusieurs (l'identification les renvoie). */
    filiale: schemaCodeFiliale.optional(),
    /** Identifiant de la conversation chez l'agent (ElevenLabs : system__conversation_id), cité dans la trace. */
    conversation: z.string().trim().max(200).optional(),
    langue: z.enum(LANGUES).optional(),
  })
  .strict();
/**
 * Fin d'appel envoyée par ElevenLabs (webhook post_call_transcription, signé) : seul le résumé de l'analyse est retenu,
 * la transcription n'est jamais lue ni conservée. Les autres champs sont ignorés.
 */
export const schemaFinAppelAgentVocal = z.looseObject({
  type: z.string(),
  data: z.looseObject({
    conversation_id: z.string().min(1),
    analysis: z.looseObject({ transcript_summary: z.string().optional().nullable() }).optional().nullable(),
  }),
});

export const VUES_INCIDENTS = ['miens', 'affectes-a-moi', 'toutes'] as const;

/**
 * Tris d'une liste d'incidents : date de création (`cree`) ou de dernière mise à jour (`modifie` : modification de
 * l'incident ou dernière réponse publique, `modifieLe` des lignes), décroissante ou croissante. Défaut : triParDefaut
 * (fin du fichier) – dernière mise à jour décroissante pour la vue « miens », création décroissante sinon.
 * Décisions de Benoit (PR 266, fil 982 : I35, I36).
 */
export const TRIS_INCIDENTS = ['cree-desc', 'cree-asc', 'modifie-desc', 'modifie-asc'] as const;
export type TriIncidents = (typeof TRIS_INCIDENTS)[number];

/**
 * Recherche : `vue` miens (je suis le demandeur), affectes-a-moi ou toutes ; `inclureArchives` : inclure aussi les
 * incidents archivés (ils sont renvoyés avec les autres, Bug #210) ;
 * `tri` : TRIS_INCIDENTS (absent : selon la vue, triParDefaut).
 */
export const schemaRechercheIncidents = z
  .object({
    filiale: z.string().optional(),
    statut: liste(schemaStatutIncident).optional(),
    priorite: liste(schemaPrioriteIncident).optional(),
    vue: z.enum(VUES_INCIDENTS).default('toutes'),
    equipeId: z.guid().optional(),
    inclureArchives: booleen.default(false),
    tri: z.enum(TRIS_INCIDENTS).optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
    suivant: z.string().optional(),
  })
  .strict();

/** Qualification : l'agent confirme ou corrige la catégorie et la priorité, et précise l'impact et l'urgence. */
export const schemaQualification = z
  .object({
    categorieId: z.guid(),
    priorite: schemaPrioriteIncident,
    impact: schemaNiveauIncident.optional(),
    urgence: schemaNiveauIncident.optional(),
  })
  .strict();

/** Requalification : catégorie et / ou priorité (au moins une) ; impact et urgence ne se changent qu'à la qualification. */
export const schemaRequalification = z
  .object({ categorieId: z.guid().optional(), priorite: schemaPrioriteIncident.optional() })
  .strict()
  .refine((r) => r.categorieId !== undefined || r.priorite !== undefined, 'Catégorie ou priorité attendue');

/** Affectation à une équipe, avec ou sans agent désigné (`affecteA: null` : équipe seule). Deux clés obligatoires. */
export const schemaAffectation = z
  .object({ equipeId: z.guid(), affecteA: schemaEmailUtilisateur.nullable() })
  .strict();

/** Prise en charge, mise en attente, reprise, clôture, réouverture : aucun champ. */
export const schemaActionSansCorps = z.object({}).strict();

/**
 * Longueur maximale de la solution d'un incident (contrainte incident_solution_longueur).
 *
 * 4 000 caractères, validés par Benoit (D-IT-074, #220) ; ReFresh n'en fixait pas.
 */
export const SOLUTION_MAX = 4000;

/** Résolution : solution appliquée, obligatoire et visible du demandeur ; jamais copiée dans l'audit. */
export const schemaResolution = z
  .object({ solution: z.string().trim().min(1, 'Obligatoire').max(SOLUTION_MAX) })
  .strict();

/**
 * Longueur maximale d'un commentaire (contrainte commentaire_texte).
 *
 * Même valeur que la solution, validée par Benoit (D-IT-074, #220).
 */
export const COMMENTAIRE_MAX = 4000;

/**
 * Nouveau commentaire : texte brut (sauts de ligne conservés), immuable une fois envoyé ; `interne` : note entre agents,
 * jamais visible du demandeur (réponse visible par défaut côté API ; le formulaire de l'agent propose la note interne).
 */
export const schemaCommentaireNouveau = z
  .object({
    texte: z.string().trim().min(1, 'Obligatoire').max(COMMENTAIRE_MAX),
    interne: z.boolean().default(false),
    /** Dépôts différés, consommés par le commentaire : ils en prennent la visibilité (note interne comprise). */
    piecesJointes: schemaIdsDepots(PIECES_JOINTES_INCIDENT_MAX).optional(),
  })
  .strict();

/**
 * Escalade vers une équipe du niveau technique supérieur (D-IT-123), avec ou sans agent désigné, et un motif
 * obligatoire, consigné en note interne (D-IT-133 : jamais visible du demandeur, jamais dans l'audit ni dans une
 * notification). Même longueur qu'un commentaire (COMMENTAIRE_MAX).
 */
export const schemaEscalade = z
  .object({
    equipeId: z.guid(),
    affecteA: schemaEmailUtilisateur.nullable().default(null),
    motif: z.string().trim().min(1, 'Obligatoire').max(COMMENTAIRE_MAX),
  })
  .strict();

// Corrections (US #141, D-IT-036) ---------------------------------------------------------------------------------
/**
 * Correction du titre, de la description ou d'un commentaire (`commentaireId` : le message d'origine) : nouveau texte,
 * mêmes longueurs qu'à la saisie. Le texte d'origine reste dans le fil (trace) ; un texte identique est refusé par
 * l'API, qui seule connaît le texte actuel (formulaires.erreurs.correctionIdentique).
 */
export const schemaCorrectionIncident = z.discriminatedUnion('cible', [
  z.object({ cible: z.literal('titre'), texte: z.string().trim().min(1, 'Obligatoire').max(TITRE_INCIDENT_MAX) }).strict(),
  z.object({ cible: z.literal('description'), texte: z.string().trim().min(1, 'Obligatoire').max(DESCRIPTION_INCIDENT_MAX) }).strict(),
  z.object({
    cible: z.literal('commentaire'), commentaireId: z.guid(), texte: z.string().trim().min(1, 'Obligatoire').max(COMMENTAIRE_MAX),
  }).strict(),
]);

/**
 * Page du fil d'un incident, du plus ancien au plus récent. Un message corrigé y figure une fois, dans sa dernière version
 * (D-IT-129) ; `versionsDe` : historique d'un message (l'original puis ses corrections), consulté à part.
 */
export const schemaRechercheCommentaires = z
  .object({
    limite: z.coerce.number().int().min(1).max(200).default(100),
    suivant: z.string().optional(),
    versionsDe: z.guid().optional(),
  })
  .strict();

// ------------------------------------------------------------------ sorties
export const schemaSlaIncident = z.object({ premiereReponse: schemaEtatSla.nullable(), resolution: schemaEtatSla.nullable() });

/** Ligne d'une liste d'incidents. Dates ISO 8601 UTC ; `sla` : état calculé à la lecture (jamais stocké). */
export const schemaIncidentLigne = z.object({
  id: z.string(),
  reference: z.string(),
  filiale: schemaCodeFiliale,
  titre: z.string(),
  statut: schemaStatutIncident,
  priorite: schemaPrioriteIncident,
  categorieId: z.string().nullable(),
  equipeId: z.string().nullable(),
  demandeur: z.string(),
  demandeurNom: z.string(),
  affecteA: z.string().nullable(),
  affecteANom: z.string().nullable(),
  echeancePremiereReponse: schemaDateIso.nullable(),
  echeanceResolution: schemaDateIso.nullable(),
  /** Première réponse au demandeur : tant qu'elle manque, la prochaine échéance du SLA est celle de la première réponse. */
  premiereReponseLe: schemaDateIso.nullable(),
  sla: schemaSlaIncident,
  confidentiel: z.boolean(),
  securite: z.boolean(),
  creeLe: schemaDateIso,
  modifieLe: schemaDateIso, // dernière mise à jour : modification de l'incident ou dernière réponse publique (I35)
});

/**
 * Fiche d'un incident : la ligne, plus le détail. `declarant` : auteur de la déclaration (peut différer du demandeur).
 * `solution` : dernière solution proposée ; conservée après une réouverture, remplacée à la résolution suivante
 * (historique des solutions : décidé, à appliquer, D-IT-032, #200). `reouvertures` : nombre de réouvertures, pour les
 * indicateurs (décision du 27/10/2026 ; non rétroactif).
 */
export const schemaIncident = schemaIncidentLigne.extend({
  description: z.string(),
  solution: z.string().nullable(),
  impact: schemaNiveauIncident.nullable(),
  urgence: schemaNiveauIncident.nullable(),
  declarant: z.string(),
  resoluLe: schemaDateIso.nullable(),
  clotureLe: schemaDateIso.nullable(),
  archiveLe: schemaDateIso.nullable(),
  visibilite: schemaVisibilite,
  reouvertures: z.number().int().nonnegative(),
});

/**
 * Commentaire d'un incident. `auteur` : e-mail en minuscules ; `auteurNom` : nom affiché à l'écriture.
 * `piecesJointes` : fichiers joints à l'envoi (renvoyés seulement avec un commentaire lisible).
 * Corrections (D-IT-036) : `correction` nul pour un message ; sinon trace d'une correction du titre ou de la description
 * (texte d'origine) ou d'un commentaire (nouveau texte, `corrigeId` = message corrigé ; renvoyée seulement dans
 * l'historique du message). `derniereVersion` : dernière correction lisible d'un message d'origine, dont le texte fait
 * référence dans le fil (D-IT-129).
 */
export const schemaCommentaire = z.object({
  id: z.string(),
  incidentId: z.string(),
  interne: z.boolean(),
  texte: z.string(),
  auteur: z.string(),
  auteurNom: z.string(),
  creeLe: schemaDateIso,
  piecesJointes: z.array(schemaPieceJointe),
  correction: z.enum(CIBLES_CORRECTION).nullable(),
  corrigeId: z.string().nullable(),
  derniereVersion: z.object({ id: z.string(), texte: z.string(), creeLe: schemaDateIso }).nullable(),
});

// ------------------------------------------------------------------ référentiels (communs au groupe)
/** Libellé multilingue d'un référentiel : français toujours présent, autres langues facultatives. */
export const schemaLibelleMultilingue = z.partialRecord(z.enum(LANGUES), z.string());

/** Catégorie ; `equipeDefautId` sert au front à pré-remplir l'affectation. */
export const schemaCategorieIt = z.object({
  id: z.string(),
  libelle: schemaLibelleMultilingue,
  parentId: z.string().nullable(),
  equipeDefautId: z.string().nullable(),
  actif: z.boolean(),
});

/** Niveau de support d'une équipe. */
export const NIVEAUX_EQUIPE = ['n1', 'n2', 'n3'] as const;

export const schemaEquipeIt = z.object({
  id: z.string(),
  code: z.string(),
  libelle: schemaLibelleMultilingue,
  niveau: z.enum(NIVEAUX_EQUIPE),
  actif: z.boolean(),
});

/**
 * Membre d'une équipe. `incidentsOuverts` : incidents de l'équipe affectés à ce membre et encore à réaffecter s'il
 * part (statuts de ACTIONS_INCIDENT.affecter), comptés dans les filiales où le lecteur a le profil support ; affiché
 * avant sa désactivation (#270, D-IT-094).
 */
export const schemaMembreEquipeIt = z.object({
  utilisateur: z.string(),
  nom: z.string(),
  actif: z.boolean(),
  incidentsOuverts: z.number().int().min(0),
});

/** Délais du SLA d'une priorité, en minutes calendaires. */
export const schemaSlaIt = z.object({
  priorite: schemaPrioriteIncident,
  premiereReponseMin: z.number().int(),
  resolutionMin: z.number().int(),
});

// ------------------------------------------------------------------ administration des référentiels (« administrer »)
/** Longueur maximale d'un libellé de référentiel, dans chaque langue. */
export const LIBELLE_REFERENTIEL_MAX = 120;

const texteLibelle = z.string().trim().max(LIBELLE_REFERENTIEL_MAX);

/**
 * Libellé saisi dans l'administration : français obligatoire, autres langues facultatives ; espaces retirés, langue
 * vide supprimée (le front se rabat alors sur le français).
 */
export const schemaLibelleSaisi = z
  .object({
    fr: texteLibelle.min(1, 'Obligatoire'),
    en: texteLibelle.optional(),
    de: texteLibelle.optional(),
    it: texteLibelle.optional(),
    es: texteLibelle.optional(),
    pt: texteLibelle.optional(),
  })
  .strict()
  .transform((l) => Object.fromEntries(LANGUES.flatMap((langue) => (l[langue] ? [[langue, l[langue]]] : []))) as Partial<Record<(typeof LANGUES)[number], string>>);

/**
 * Nouvelle catégorie : racine (sans `parentId`) ou sous-catégorie d'une racine active. L'équipe par défaut est
 * obligatoire (US #73 : « chaque catégorie porte une équipe par défaut »).
 */
export const schemaCategorieNouvelle = z
  .object({ libelle: schemaLibelleSaisi, parentId: z.guid().nullable().default(null), equipeDefautId: z.guid() })
  .strict();

/**
 * Modification d'une catégorie (au moins un champ). Le parent ne change pas après la création.
 *
 * Un déplacement fausserait l'analyse des incidents récurrents (D-IT-092, validé par Benoit : #268).
 */
export const schemaCategorieModification = z
  .object({ libelle: schemaLibelleSaisi.optional(), equipeDefautId: z.guid().optional(), actif: z.boolean().optional() })
  .strict()
  .refine((c) => Object.values(c).some((v) => v !== undefined), 'Aucun changement');

/**
 * Nouvelle équipe. Le code (N1, SAGE-X3…) ne change plus ensuite : il identifie l'équipe dans les échanges. Une
 * équipe n'est jamais vide (#240) : elle se crée avec son premier membre, e-mail d'un utilisateur AGORA actif.
 */
export const schemaEquipeNouvelle = z
  .object({
    code: z.string().trim().toUpperCase().pipe(z.string().regex(/^[A-Z0-9-]{2,20}$/, 'Code attendu')),
    libelle: schemaLibelleSaisi,
    niveau: z.enum(NIVEAUX_EQUIPE),
    premierMembre: schemaEmailUtilisateur,
  })
  .strict();

/** Modification d'une équipe (au moins un champ). */
export const schemaEquipeModification = z
  .object({ libelle: schemaLibelleSaisi.optional(), niveau: z.enum(NIVEAUX_EQUIPE).optional(), actif: z.boolean().optional() })
  .strict()
  .refine((e) => Object.values(e).some((v) => v !== undefined), 'Aucun changement');

/** Ajout d'un membre : e-mail d'un utilisateur AGORA actif ; son nom est lu dans l'annuaire, jamais saisi. */
export const schemaMembreAjout = z.object({ utilisateur: schemaEmailUtilisateur }).strict();

/** Activation / désactivation d'un membre (e-mail dans le corps, jamais dans l'adresse). */
export const schemaMembreStatut = z.object({ utilisateur: schemaEmailUtilisateur, actif: z.boolean() }).strict();

const delaiSla = z.number().int().min(1).max(SLA_DELAI_MAX_MIN);

/** Modification des délais d'une priorité (au moins un) ; la résolution ne précède jamais la première réponse. */
export const schemaSlaModification = z
  .object({ premiereReponseMin: delaiSla.optional(), resolutionMin: delaiSla.optional() })
  .strict()
  .refine((d) => d.premiereReponseMin !== undefined || d.resolutionMin !== undefined, 'Aucun changement')
  .refine(
    (d) => d.premiereReponseMin === undefined || d.resolutionMin === undefined || d.resolutionMin >= d.premiereReponseMin,
    { message: 'formulaires.erreurs.resolutionAvantReponse', path: ['resolutionMin'] },
  );

/** Durée saisie dans l'écran : heures (0 à 9999) et minutes (0 à 59), en texte ; vide = 0. */
const schemaDureeSaisie = z.object({
  heures: z.string().trim().regex(/^\d{0,4}$/),
  minutes: z.string().trim().regex(/^([0-5]?\d)?$/),
}).strict();
const minutesSaisies = (d: z.output<typeof schemaDureeSaisie>) => Number(d.heures || 0) * 60 + Number(d.minutes || 0);

/**
 * Saisie des délais d'une priorité dans l'écran d'administration (heures et minutes calendaires), convertie en
 * minutes : mêmes bornes que l'API (1 minute à SLA_DELAI_MAX_MIN, résolution ≥ première réponse), erreurs placées sur
 * le champ « heures » de chaque délai. Sortie = corps de PATCH /v1/it/incidents/sla/{priorite}.
 */
export const schemaSlaSaisie = z
  .object({ premiereReponse: schemaDureeSaisie, resolution: schemaDureeSaisie })
  .strict()
  .superRefine((v, ctx) => {
    const [p, r] = [minutesSaisies(v.premiereReponse), minutesSaisies(v.resolution)];
    const horsBornes = (m: number) => m < 1 || m > SLA_DELAI_MAX_MIN;
    if (horsBornes(p)) ctx.addIssue({ code: 'custom', path: ['premiereReponse', 'heures'], message: 'formulaires.erreurs.delaiSla' });
    if (horsBornes(r)) ctx.addIssue({ code: 'custom', path: ['resolution', 'heures'], message: 'formulaires.erreurs.delaiSla' });
    else if (!horsBornes(p) && r < p) {
      ctx.addIssue({ code: 'custom', path: ['resolution', 'heures'], message: 'formulaires.erreurs.resolutionAvantReponse' });
    }
  })
  .transform((v) => ({ premiereReponseMin: minutesSaisies(v.premiereReponse), resolutionMin: minutesSaisies(v.resolution) }));

// ------------------------------------------------------------------ tri par défaut d'une liste
/**
 * Tri d'une liste d'incidents sans `tri` explicite : la vue « miens » (le demandeur suit ses incidents) arrive triée par
 * dernière mise à jour, la plus récente d'abord (décision de Benoit, PR 266, fil 982, I36) ; les autres vues, par
 * création, la plus récente d'abord. Partagé par l'API, l'outil MCP, l'écran et la maquette.
 */
export function triParDefaut(vue: (typeof VUES_INCIDENTS)[number]): TriIncidents {
  return vue === 'miens' ? 'modifie-desc' : 'cree-desc';
}
